import { useState } from "react";

import { useKioskSaveDiaryMutation } from "../api/klassenbuchKioskApi";
import type { ClassWeekCacheKey, KioskClassWeekCell } from "../types/kioskApiTypes";

/**
 * `userId` is required: it's the teacher's user_id (from
 * fn_kiosk_chip_login response) and must be passed so the diary
 * RPC records the correct `created_by` / `updated_by` instead of
 * defaulting to auth.uid() (which on a paired kiosk = kiosk_id,
 * not the human). Authenticated browser-tab callers pass their
 * own auth uid here — value is the same as the RPC would derive
 * itself, so behaviour is unchanged for non-kiosk.
 */
export function useKioskDiarySave(
  cell: KioskClassWeekCell,
  classWeekKey: ClassWeekCacheKey,
  userId: string,
) {
  const [saveDiary, { isLoading: isSaving }] = useKioskSaveDiaryMutation();
  const [savedFlash, setSavedFlash] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const trySave = async (nextInhalt: string, nextHausaufgabe: string) => {
    const dirty =
      nextInhalt.trim() !== (cell.diary_text ?? "").trim() ||
      (nextHausaufgabe.trim() || "") !== (cell.diary_homework ?? "").trim();
    if (!dirty) return;
    setSaveError(null);
    try {
      await saveDiary({
        lessonId: cell.lesson_id,
        userId,
        entryText: nextInhalt.trim(),
        homework: nextHausaufgabe.trim() || null,
        _classWeekKey: classWeekKey,
      }).unwrap();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
    } catch (e) {
      const msg =
        (e as { error?: { message?: string }; message?: string })?.error?.message ??
        (e as { message?: string })?.message ??
        "Speichern fehlgeschlagen";
      setSaveError(msg);
    }
  };

  return { trySave, isSaving, savedFlash, saveError };
}
